import React, {useState, useEffect, useRef} from 'react';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {Pagination, Loader, Button, Input, Icon} from 'semantic-ui-react';
import {withRouter} from 'react-router-dom';
import _ from 'lodash';
import BudgetSummaryBySubrecipientTable from './BudgetSummaryBySubrecipientTable';
import {BudgetUtils} from 'app-ui-componentlibrary/dist/go/components/common/BudgetUtils';
import {PERFORMING_ORG_ID_PATH, COST} from 'common/requestDetails/constants';
import {Alert} from 'componentLibrary';

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const BudgetSummaryBySubrecipientTableWrapper = (props) => {
  const {organizationId, subOrgList, budget, fundingStreams} = props;
  const pageSize = _.get(props, 'pageSize', 10);
  const defaultSort = _.get(props, 'defaultSort', 'subrecipient');
  const defaultSortDirection = _.get(props, 'defaultSortDirection', 'ASC');

  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [requestQueue, setRequestQueue] = useState([]);
  const [tableData, setTableData] = useState({});
  const newItems = _.get(tableData, 'items', []);

  const [selectedColumn, setSelectedColumn] = useState(defaultSort);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isClearingSearchTerm, setIsClearingSearchTerm] = useState(false);
  const [tableHeight, setTableHeight] = useState('63px');
  const prevSearchTerm = usePrevious(searchTerm);
  const tableRef = useRef(null);
  const requestQueueRef = useRef();
  requestQueueRef.current = requestQueue;

  const activeFilteredCount = _.get(tableData, 'count', 0);
  const totalPages = Math.ceil(activeFilteredCount / pageSize);
  let showPrevButton = currentPage + 1 > 1 ? undefined : null;
  let showNextButton = currentPage + 1 < totalPages ? undefined : null;

  const [filteredSubOrgList, setFilteredSubOrgList] = useState([]);

  const setSelectedColumnProp = (columnName) => {
    setSelectedColumn(columnName);
  };

  const setSortDirectionProp = (direction) => {
    setSortDirection(direction);
    setCurrentPage(0);
  };

  const handlePageChange = (e, {activePage}) => {
    setCurrentPage(activePage - 1);
  };

  const handleTermChange = (e, entry) => {
    setSearchTerm(entry.value);
    setCurrentPage(0);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    getSubOrgContents();
  };

  const onClearSearchTerm = () => {
    setIsClearingSearchTerm(true);
    setSearchTerm('');
  };

  useEffect(() => {
    if (organizationId && subOrgList) {
      const initialList = _.cloneDeep(subOrgList);
      delete initialList.loading;
      const filteredSubOrgList = _.filter(
        initialList,
        (subOrg) => subOrg.organizationId != organizationId
      );
      setFilteredSubOrgList(filteredSubOrgList);
      getSubOrgContents();
    }
  }, [subOrgList]);

  useEffect(() => {
    if (!isInitialLoad) {
      getSubOrgContents();
    }
  }, [selectedColumn, sortDirection, currentPage, budget, fundingStreams]);

  useEffect(() => {
    if (prevSearchTerm !== searchTerm && !isInitialLoad) {
      if (isClearingSearchTerm) {
        getSubOrgContents();
        setIsClearingSearchTerm(false);
      } else {
        const timer = setTimeout(() => {
          getSubOrgContents();
        }, 1200);

        return () => clearTimeout(timer);
      }
    }
  }, [prevSearchTerm, searchTerm, isClearingSearchTerm]);

  const getSubOrgContents = () => {
    setLoading(true);
    setRequestQueue([
      ...requestQueue,
      {
        type: 'getSubOrgsAction',
      },
    ]);
  };

  useEffect(() => {
    if (requestQueue.length && !requestQueue[0].loading) {
      const action = {
        ...requestQueue[0],
      };
      action.loading = true;
      setRequestQueue([action, ...requestQueue.slice(1)]);
      const cb = () => {
        setRequestQueue(requestQueueRef.current.slice(1));
      };
      switch (action.type) {
        case 'getSubOrgsAction':
          getSubOrgsContentsAction(cb);
          break;
      }
    }
  }, [requestQueue]);

  const getSubOrgsContentsAction = (cb) => {
    setLoading(true);
    const offset = currentPage * pageSize;

    if (_.get(tableRef, 'current')) {
      let height = tableRef.current.getBoundingClientRect().height;
      setTableHeight(height);
    }

    const subOrgData = getSubOrgData(searchTerm, pageSize, offset);
    setTableData(subOrgData);
    setLoading(false);
    if (isInitialLoad) setIsInitialLoad(false);
    cb();
  };

  const getSubOrgMACost = (item) => {
    let costItems = BudgetUtils.collectItemsContainingFieldPath([], COST, item);
    let subOrgCost = 0;
    _.forEach(costItems, (costItem) => {
      const solutionArea = _.get(costItem, 'additionalInformation.solutionArea');
      if (solutionArea === 'MANAGEMENT_AND_ADMINISTRATION') {
        const costs = _.get(costItem, COST, []);
        _.forEach(costs, (costAry) => {
          const total = _.get(costAry, '0.total');
          subOrgCost += total;
        });
      }
    });
    return subOrgCost;
  };

  const collectMgmtCostForSubOrgs = (itemsWithOrgId, parentOrgId) => {
    const subOrgItems = new Map();
    _.forEach(itemsWithOrgId, (item) => {
      const orgId = _.toNumber(_.get(item, PERFORMING_ORG_ID_PATH));
      if (orgId && orgId != parentOrgId) {
        let subOrgCost = getSubOrgMACost(item);
        let existingCost = subOrgItems.get(orgId);
        if (existingCost) subOrgCost = subOrgCost + existingCost;
        subOrgItems.set(orgId, subOrgCost);
      }
    });
    return subOrgItems;
  };

  const getSubOrgData = (term, pageSize, offset) => {
    const {budget, fundingStreams} = props;

    const itemsWithOrgId = BudgetUtils.collectItemsContainingFieldPath(
      [],
      PERFORMING_ORG_ID_PATH,
      fundingStreams
    );
    const subOrgItems = collectMgmtCostForSubOrgs(
      itemsWithOrgId,
      _.toNumber(organizationId)
    );
    const subrecipientSummaryList = _.get(budget, 'subrecipientSumary.groupByValues', {});

    let filteredArr = [];
    _.forEach(filteredSubOrgList, (subOrg) => {
      const subOrgBudget = subrecipientSummaryList[subOrg.organizationId];
      const subOrgTotalCosts = _.get(subOrgBudget, 'total');
      const subOrgMaxAllowableMgmtCosts = subOrgTotalCosts * 0.05;
      const subOrgMACosts = subOrgItems.has(subOrg.organizationId)
        ? subOrgItems.get(subOrg.organizationId)
        : 0;
      const item = {
        ...subOrg,
        subOrgTotalCosts,
        subOrgMaxAllowableMgmtCosts,
        subOrgMACosts,
      };
      filteredArr.push(item);
    });

    let filteredItems = [];

    if (term) {
      _.forEach(filteredArr, (subOrg) => {
        if (_.get(subOrg, 'displayName', '').toLowerCase().includes(term.toLowerCase())) {
          filteredItems.push(subOrg);
        }
      });
    } else {
      filteredItems = filteredArr;
    }

    filteredItems = sortItems(filteredItems, selectedColumn, sortDirection);

    const count = filteredItems.length;
    filteredItems = _.slice(filteredItems, offset, offset + pageSize);

    filteredItems.map(item => {
        const isError = item.subOrgMACosts > 0 ? item.subOrgMACosts > item.subOrgMaxAllowableMgmtCosts:false;
        if (isError) {
            item.isError = true;
            item.errormessage = `${item.displayName} subrecipient management costs may not exceed 5% of the total subrecipient costs.`;
        }
    });

    const subOrgData = {
      count,
      items: filteredItems,
    };
    return subOrgData;
  };

  const sortItems = (items, sortBy, sortDirection) => {
    let sortedItems;

    if (sortBy === 'subrecipient') {
      sortedItems = _.sortBy(items, 'displayName');
      if (sortDirection === 'DESC') sortedItems = sortedItems.reverse();
    }

    if (sortBy === 'maCosts') {
      sortedItems = _.sortBy(items, 'subOrgMACosts');
      if (sortDirection === 'DESC') sortedItems = sortedItems.reverse();
    }

    if (sortBy === 'subRecipTotalCosts') {
      sortedItems = _.sortBy(items, 'subOrgTotalCosts');
      if (sortDirection === 'DESC') sortedItems = sortedItems.reverse();
    }

    return sortedItems;
  };

  return (
    <>
      <div className="search-bar">
        <form id="search-form" onSubmit={onSearch}>
          <span>
            <Input>
              <Input
                aria-label={`Search by term`}
                title={`Search by term`}
                value={searchTerm}
                onChange={handleTermChange}
                onFocus={(e) => e.target.select()}
                style={{width: '380px'}}
              />
              <Button primary icon aria-label="Search Button" title="Search button">
                <Icon name="search" fitted />
              </Button>
            </Input>
            {searchTerm !== '' && (
              <a
                id="clear-search"
                href="#"
                className="padding-left"
                onClick={() => onClearSearchTerm()}
              >
                Clear search
              </a>
            )}
          </span>
        </form>
      </div>
      {loading ? (
        <div ref={tableRef}>
          <BudgetSummaryBySubrecipientTable
            subOrgs={[]}
            setSelectedColumnProp={setSelectedColumnProp}
            setSortDirectionProp={setSortDirectionProp}
            selectedColumn={selectedColumn}
            sortDirection={sortDirection}
          />
        </div>
      ) : (
        <>
          <div ref={tableRef}>
            <BudgetSummaryBySubrecipientTable
              subOrgs={newItems}
              setSelectedColumnProp={setSelectedColumnProp}
              setSortDirectionProp={setSortDirectionProp}
              selectedColumn={selectedColumn}
              sortDirection={sortDirection}
            />

            {_.size(newItems) < 1 && (
              <div id="no-suborgs-found" className="text-align-center">
                There are no subrecipients with the given search parameter.
              </div>
            )}

            {activeFilteredCount > pageSize && (
              <Pagination
                activePage={currentPage + 1}
                siblingRange={1}
                boundaryRange={1}
                prevItem={showPrevButton}
                nextItem={showNextButton}
                firstItem={showPrevButton}
                lastItem={showNextButton}
                ellipsisItem={undefined}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
          <div>{newItems && newItems.length > 0 && (
          <div id='alert' >
            {newItems.map((item) => {
              return (
                <div key={item.organizationId}>
                  {item.isError && (
                      <Alert
                            size="small"
                            header={'Exceeds subrecipient management cost limit'}
                            type="error"
                            dismissable={false}
                          >
                            {item.errormessage}
                          </Alert>
                  )}
                </div>
              );
            })}
         </div>
          )}
            </div>
        </>
      )}
                        
    </>
  );
};

const BudgetSummaryBySubrecipientTableWrapperWithRouter = withRouter(
  BudgetSummaryBySubrecipientTableWrapper
);

export default connectDynamic(BudgetSummaryBySubrecipientTableWrapperWithRouter);
